<template>
  <f7-page name="group-documents" @page:init="load">
    <f7-navbar back-link>
      <f7-nav-title class="vault-nav-title">
        {{ $t('common.documents') }}
      </f7-nav-title>
      <f7-nav-right>
        <f7-button
          v-if="viewerCanManage"
          class="vault-upload-btn"
          fill
          round
          size="small"
          @click="openUploadSheet"
          :aria-label="$t('documents.uploadDocument')"
        >
          <f7-icon material="upload_file" :size="24" />
        </f7-button>
      </f7-nav-right>
    </f7-navbar>

    <AppLayout>
      <template #main>
        <!-- Coffre-Fort Banner Header -->
        <section class="vault-section">
          <div class="vault-card">
            <div class="vault-card-head">
              <div class="vault-brand">
                <span class="material-symbols-outlined vault-brand-icon" style="font-variation-settings: 'FILL' 1;">shield_lock</span>
                <span class="vault-brand-label">{{ $t('documents.decentralizedVault') }}</span>
              </div>
              <span class="vault-chip">
                <f7-icon material="lock" :size="14" /> {{ $t('documents.encryptedAes') }}
              </span>
            </div>
            <div class="vault-card-body">
              <h1 class="vault-title">
                {{ $t('documents.vaultTitle') }}
              </h1>
              <p class="vault-subtitle">
                {{ $t('documents.vaultSubtitle') }}
              </p>
            </div>
            <!-- Storage Capacity Bar -->
            <div class="vault-storage">
              <div class="vault-storage-labels">
                <span class="vault-storage-label">
                  <f7-icon material="cloud_done" :size="16" />
                  {{ $t('documents.groupStorage') }}
                </span>
                <span class="vault-storage-value">{{ $t('documents.storageUsage', { used: $t('documents.storageUsedValue'), total: $t('documents.storageTotalValue'), pct: 24 }) }}</span>
              </div>
              <f7-progressbar class="storage-progress" :progress="24" />
            </div>
          </div>
        </section>

        <!-- Main Upload Action CTA -->
        <section v-if="viewerCanManage" class="vault-cta-section">
          <f7-button round fill large raised
            @click="openUploadSheet"
          >
            <f7-icon material="upload_file" :size="20" />
            <span>{{ $t('documents.uploadDocument') }}</span>
          </f7-button>
        </section>

        <!-- Category filter chips -->
        <div class="filter-row">
          <f7-link
            v-for="cat in categories"
            :key="cat.id"
            class="filter-chip"
            :class="activeCategory === cat.id ? 'filter-chip--active' : 'filter-chip--idle'"
            @click="selectCategory(cat.id)"
          >
            <span
              v-if="cat.materialIcon !== 'draft'"
              class="filter-chip-icon"
              :style="activeCategory !== cat.id ? { color: cat.color } : null"
            >
              <f7-icon :material="cat.materialIcon" :size="16" />
            </span>
            <span
              v-else
              class="material-symbols-outlined filter-chip-icon"
              style="font-size: 16px"
              :style="activeCategory !== cat.id ? { color: cat.color } : null"
            >{{ cat.materialIcon }}</span>
            <span>{{ $t(cat.labelKey) }}</span>
          </f7-link>
        </div>

        <f7-block v-if="documentsStore.loading" class="state-block">
          <f7-preloader color="green" />
        </f7-block>

        <f7-block v-else-if="documentsStore.documents.length === 0" class="state-block state-block--empty">
          <f7-icon material="folder_open" :size="48" class="empty-icon" />
          <p class="empty-text">{{ $t('documents.empty') }}</p>
          <p v-if="viewerCanManage" class="empty-hint">{{ $t('documents.emptyHint') }}</p>
        </f7-block>

        <f7-list v-else inset strong>
          <f7-list-item
            v-for="doc in documentsStore.documents"
            :key="doc.id"
            :title="doc.fileName"
            link
            @click="openPreview(doc)"
          >
            <template #media>
              <div
                class="doc-type-icon"
                :style="{ background: documentCategoryMeta(doc.category).color + '22', color: documentCategoryMeta(doc.category).color }"
              >
                <component
                  :is="getDocIconComponent(documentCategoryMeta(doc.category).icon)"
                  :f7="documentCategoryMeta(doc.category).icon"
                  size="22"
                />
              </div>
            </template>
            <template #subtitle>
              <div class="doc-subtitle">
                <span>{{ memberName(doc.createdBy) }}</span>
                <span>·</span>
                <span>{{ fmtSize(doc.size) }}</span>
                <span>·</span>
                <span>{{ $dayjs(doc.created).format('ll') }}</span>
              </div>
            </template>
            <template #after>
              <div class="doc-visibility">
                <f7-icon :f7="documentVisibilityMeta(doc.visibility).icon" size="12" />
                <span>{{ $t(documentVisibilityMeta(doc.visibility).labelKey) }}</span>
              </div>
            </template>
          </f7-list-item>
        </f7-list>
      </template>
    </AppLayout>

    <!-- Upload sheet (admin/owner only) -->
    <AdaptiveSheet v-model:opened="uploadSheetOpen" swipe-to-close :tablet-fullscreen="false" close-by-outside-click close-on-escape>
      <f7-page-content>
      
        <div class="card-header">
          <f7-block-title>{{ $t('documents.addDocument') }}</f7-block-title>
          <f7-link sheet-close popup-close style="right:8px"><f7-icon f7="multiply" /></f7-link>
        </div>

        <input ref="fileInput" type="file" multiple style="display: none" @change="onFilesPicked" />

        <f7-list inset strong>
          <f7-list-item link @click="fileInput?.click()" :title="$t('common.uploadFiles')">
            <template #media><f7-icon f7="plus_circle_fill" color="blue" /></template>
          </f7-list-item>
          <f7-list-item
            v-for="(pending, index) in pendingUploads"
            :key="pending.fileId"
            :title="pending.fileName"
            :subtitle="fmtSize(pending.size)"
          >
            <template #media>
              <component
                :is="getDocIconComponent(documentCategoryMeta(pendingCategory(pending)).icon)"
                :f7="documentCategoryMeta(pendingCategory(pending)).icon"
                size="18"
              />
            </template>
            <template #after>
              <f7-link icon-f7="xmark_circle_fill" color="red" @click="removePending(index)" />
            </template>
          </f7-list-item>
        </f7-list>

        <f7-block-title>{{ $t('documents.visibility') }}</f7-block-title>
        <f7-list inset strong>
          <f7-list-item
            v-for="level in DOCUMENT_VISIBILITY_LEVELS"
            :key="level.id"
            :title="$t(level.labelKey)"
            :subtitle="$t(level.descKey)"
            radio
            :name="'uploadVisibility'"
            :checked="uploadVisibility === level.id"
            @click="uploadVisibility = level.id"
          >
            <template #media>
              <div class="doc-type-icon" :style="{ background: level.color + '22', color: level.color }">
                <f7-icon :f7="level.icon" size="18" />
              </div>
            </template>
          </f7-list-item>
        </f7-list>

        <f7-block>
          <f7-button large fill round raised :disabled="documentsStore.uploading || pendingUploads.length === 0" @click="doUpload">
            <f7-preloader v-if="documentsStore.uploading" size="20" color="white" />
            <span v-else>{{ $t('documents.upload') }}</span>
          </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>

    <!-- Preview popup -->
    <AdaptiveSheet v-model:opened="previewOpen" :tablet-fullscreen="true" full-screen close-by-outside-click close-on-escape>
      <f7-view>
        <f7-page>
          <f7-navbar :title="previewDoc?.fileName" sliding>
            <f7-nav-right>
              <f7-link sheet-close popup-close><f7-icon f7="multiply" /></f7-link>
            </f7-nav-right>
          </f7-navbar>

          <div class="preview-body">
            <img
              v-if="previewDoc && isImage(previewDoc)"
              :src="previewDoc.url"
              :alt="previewDoc.fileName"
              class="preview-media"
            />
            <video
              v-else-if="previewDoc && isVideo(previewDoc)"
              :src="previewDoc.url"
              controls
              class="preview-media"
            />
            <audio
              v-else-if="previewDoc && isAudio(previewDoc)"
              :src="previewDoc.url"
              controls
              class="preview-audio"
            />
            <iframe
              v-else-if="previewDoc && isPdf(previewDoc)"
              :src="previewDoc.url"
              class="preview-iframe"
            />
            <div v-else-if="previewDoc" class="preview-placeholder">
              <component
                :is="getDocIconComponent(documentCategoryMeta(previewDoc.category).icon)"
                :f7="documentCategoryMeta(previewDoc.category).icon"
                size="64"
              />
              <p class="preview-placeholder-text">{{ $t('documents.noPreview') }}</p>
            </div>

            <f7-list inset strong class="preview-info">
              <f7-list-item :title="$t('common.description')">
                <template #after>
                  <span class="preview-value">{{ previewDoc?.alt || previewDoc?.fileName }}</span>
                </template>
              </f7-list-item>
              <f7-list-item :title="$t('documents.uploadedBy')">
                <template #after>
                  <span class="preview-value">{{ memberName(previewDoc?.createdBy) }}</span>
                </template>
              </f7-list-item>
              <f7-list-item :title="$t('common.documents')">
                <template #after>
                  <span class="preview-value">{{ fmtSize(previewDoc?.size) }}</span>
                </template>
              </f7-list-item>
              <f7-list-item v-if="viewerCanManage" :title="$t('documents.visibility')">
                <template #after>
                  <div class="doc-visibility">
                    <f7-icon :f7="documentVisibilityMeta(previewDoc?.visibility).icon" size="12" />
                    <span>{{ $t(documentVisibilityMeta(previewDoc?.visibility).labelKey) }}</span>
                  </div>
                </template>
              </f7-list-item>
            </f7-list>

            <f7-block v-if="viewerCanManage" class="preview-actions">
              <f7-block-title>{{ $t('documents.changeVisibility') }}</f7-block-title>
              <f7-list inset strong>
                <f7-list-item
                  v-for="level in DOCUMENT_VISIBILITY_LEVELS"
                  :key="level.id"
                  :title="$t(level.labelKey)"
                  radio
                  :name="'editVisibility'"
                  :checked="editVisibility === level.id"
                  @click="editVisibility = level.id"
                />
              </f7-list>
              <div class="preview-action-row">
                <f7-button small raised fill @click="saveVisibility">
                  <f7-icon f7="checkmark_alt" size="14" /> {{ $t('common.save') }}
                </f7-button>
                <f7-button small raised outline color="red" @click="deleteDoc">
                  <f7-icon f7="trash" size="14" /> {{ $t('common.delete') }}
                </f7-button>
              </div>
            </f7-block>

            <f7-block class="preview-actions">
              <f7-button fill small @click="downloadDoc">
                <f7-icon f7="arrow_down_circle_fill" size="14" /> {{ $t('common.download') }}
              </f7-button>
            </f7-block>
          </div>
        </f7-page>
      </f7-view>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, computed } from 'vue'
import { f7 } from 'framework7-vue'
import { utils } from 'framework7'
import AppLayout from '../components/common/AppLayout.vue'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'
import { useDocumentsStore } from '../js/pinia-stores/documents.js'
import { useMembersStore } from '../js/pinia-stores/members.js'
import { useKeycloakStore } from '../js/pinia-stores/keycloak.js'
import { useAuthStore } from '../js/pinia-stores/auth.js'
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_VISIBILITY_LEVELS,
  documentCategoryMeta,
  documentVisibilityMeta,
  fmtSize,
} from '../constants/document-categories.js'
import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({
  groupId: { type: String, required: true },
})

const documentsStore = useDocumentsStore()
const membersStore = useMembersStore()
const keycloakStore = useKeycloakStore()

const activeCategory = ref('')
const uploadSheetOpen = ref(false)
const pendingUploads = ref([])
const uploadVisibility = ref('MEMBERS')
const fileInput = ref(null)
const previewOpen = ref(false)
const previewDoc = ref(null)
const editVisibility = ref('MEMBERS')

const viewerCanManage = computed(() => {
  const viewer = membersStore.members.find((m) => m.userId === keycloakStore.subject)
  return viewer?.role === 'OWNER' || viewer?.role === 'ADMIN'
})

const categories = computed(() => [
  { id: '', labelKey: 'common.all', icon: 'tray_full_fill', materialIcon: 'folder_open', color: '#1a6b42' },
  ...DOCUMENT_CATEGORIES,
])

// Helper to determine icon component for dynamic document category icons
// video_fill is MISSING both in Framework7, so we render it as a material-symbols-outlined span
function getDocIconComponent(iconName) {
  return iconName === 'video_fill' ? 'span' : 'f7-icon'
}

function downloadDoc() {
  if (!previewDoc.value) return
  const a = document.createElement('a')
  a.href = previewDoc.value.url
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  a.download = previewDoc.value.fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function selectCategory(id) {
  activeCategory.value = id
  loadDocuments()
}

function memberName(userId) {
  if (!userId) return ''
  const m = membersStore.members.find((x) => x.userId === userId)
  if (m?.name) return m.name
  if (userId === keycloakStore.subject) return t('documents.you')
  return userId.slice(0, 8)
}

function pendingCategory(pending) {
  if (!pending?.contentType) return 'OTHER'
  const mime = pending.contentType
  if (mime === 'application/pdf') return 'PDF'
  if (mime.startsWith('image/')) return 'IMAGE'
  if (mime.startsWith('video/')) return 'VIDEO'
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return 'SPREADSHEET'
  if (mime.includes('word') || mime === 'text/plain') return 'DOCUMENT_TEXT'
  if (mime.includes('zip') || mime.includes('compressed') || mime.includes('rar')) return 'ARCHIVE'
  return 'OTHER'
}

function deriveFileType(contentType) {
  if (!contentType) return 'file'
  if (contentType.startsWith('image/')) return 'image'
  if (contentType.startsWith('video/')) return 'video'
  if (contentType.startsWith('audio/')) return 'audio'
  return 'file'
}

const isImage = (doc) => doc.contentType?.startsWith('image/') || doc.fileType === 'image'
const isVideo = (doc) => doc.contentType?.startsWith('video/') || doc.fileType === 'video'
const isAudio = (doc) => doc.contentType?.startsWith('audio/') || doc.fileType === 'audio'
const isPdf = (doc) => doc.contentType === 'application/pdf' || doc.category === 'PDF'

async function load() {
  await Promise.all([loadDocuments(), membersStore.fetchMembers(props.groupId).catch(() => {})])
}

async function loadDocuments() {
  try {
    await documentsStore.fetchDocuments(props.groupId, activeCategory.value)
  } catch (err) {
    f7.dialog.alert(err?.response?.data?.message || t('documents.loadFailed'))
  }
}

function openUploadSheet() {
  pendingUploads.value = []
  uploadVisibility.value = 'MEMBERS'
  uploadSheetOpen.value = true
}

function onFilesPicked(event) {
  const files = Array.from(event.target.files || [])
  event.target.value = null
  for (const file of files) {
    pendingUploads.value.push({
      file,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
      fileType: deriveFileType(file.type),
      alt: file.name,
      fileId: `${utils.id('xxxx-xxxx-xxxx-xxxx')}/${file.name}`,
      url: window.URL.createObjectURL(file),
    })
  }
}

function removePending(index) {
  pendingUploads.value.splice(index, 1)
}

async function doUpload() {
  if (pendingUploads.value.length === 0) return
  documentsStore.uploading = true
  try {
    const authStore = useAuthStore()
    authStore.clearDocumentsUrl()
    pendingUploads.value.forEach((d) => authStore.setDocumentsUrl({ ...d }))
    const shortId = utils.id()
    await authStore.postR2Document({
      shortId,
      bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
    })

    const uploaded = authStore.documentsUrl.filter((d) => d.url && !d.url.startsWith('blob:'))
    for (const doc of uploaded) {
      const original = pendingUploads.value.find((p) => p.fileName === doc.fileName)
      await documentsStore.registerDocument(props.groupId, {
        fileName: doc.fileName,
        contentType: doc.contentType,
        fileType: original?.fileType || doc.fileType || deriveFileType(doc.contentType),
        size: doc.size || original?.size || 0,
        fileId: original?.fileId || doc.fileId,
        url: doc.url,
        alt: doc.alt || doc.fileName,
        visibility: uploadVisibility.value,
      })
    }

    authStore.clearDocumentsUrl()
    pendingUploads.value = []
    uploadSheetOpen.value = false
    f7.toast.show({ text: t('documents.uploaded'), closeTimeout: 1500 })
    await loadDocuments()
  } catch (err) {
    f7.dialog.alert(err?.response?.data?.message || t('documents.uploadFailed'))
  } finally {
    documentsStore.uploading = false
  }
}

function openPreview(doc) {
  previewDoc.value = doc
  editVisibility.value = doc.visibility
  previewOpen.value = true
}

async function saveVisibility() {
  const doc = previewDoc.value
  if (!doc) return
  if (editVisibility.value === doc.visibility) {
    previewOpen.value = false
    return
  }
  try {
    await documentsStore.updateDocument(props.groupId, doc.id, { visibility: editVisibility.value })
    previewOpen.value = false
    f7.toast.show({ text: t('documents.updated'), closeTimeout: 1500 })
    await loadDocuments()
  } catch (err) {
    f7.dialog.alert(err?.response?.data?.message || t('documents.updateFailed'))
  }
}

function deleteDoc() {
  const doc = previewDoc.value
  if (!doc) return
  f7.dialog.confirm(
    t('documents.deleteConfirm', { fileName: doc.fileName }),
    async () => {
      try {
        await documentsStore.deleteDocument(props.groupId, doc.id)
        previewOpen.value = false
        f7.toast.show({ text: t('documents.deleted'), closeTimeout: 1500 })
        await loadDocuments()
      } catch (err) {
        f7.dialog.alert(err?.response?.data?.message || t('documents.deleteFailed'))
      }
    },
  )
}
</script>

<style scoped>
/* All colors resolve through theme-flipping tokens (defined for :root and
   .dark in app.css) — one rule set serves light and dark mode. */
.vault-nav-title {
  font-size: var(--f7-headline-sm-font-size, 16px);
  font-weight: 700;
  color: var(--f7-theme-color, #1a6b42);
}
.vault-upload-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Coffre-fort banner */
.vault-section {
  padding: 12px 16px 8px;
}
.vault-card {
  background: var(--f7-surface-container-low, #edf6ed);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06), 0 1px 3px rgba(44, 52, 46, 0.06);
  color: var(--f7-on-surface, #161d18);
}
.vault-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.vault-brand {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.vault-brand-icon {
  font-size: 22px;
  color: var(--f7-theme-color, #1a6b42);
}
.vault-brand-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--f7-theme-color, #1a6b42);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.vault-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  color: var(--f7-secondary, #755b00);
  background: var(--f7-card-bg-color, #fff);
  box-shadow: 0 1px 3px rgba(44, 52, 46, 0.06);
  flex-shrink: 0;
}
.vault-card-body {
  margin-top: 8px;
}
.vault-title {
  margin: 0;
  font-size: var(--f7-headline-sm-font-size, 16px);
  font-weight: 700;
  line-height: 1.35;
}
.vault-subtitle {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--f7-on-surface-variant, #3f4941);
}
.vault-storage {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid color-mix(in srgb, var(--avocash-outline, #6f7a71) 30%, transparent);
}
.vault-storage-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.vault-storage-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #3f4941);
}
.vault-storage-label .f7-icon {
  color: var(--f7-theme-color, #1a6b42);
}
.vault-storage-value {
  font-size: 11px;
  font-weight: 600;
}
.vault-storage-value .vault-storage-total {
  font-weight: 400;
  color: var(--f7-on-surface-variant, #6f7a71);
}
.storage-progress {
  --f7-progressbar-height: 2px;
  --f7-progressbar-bg-color: var(--f7-surface-container-highest, #dce5dc);
  --f7-progressbar-color: var(--f7-theme-color, #1a6b42);
}

/* Upload CTA */
.vault-cta-section {
  margin: 8px 16px;
}

/* Category filter chips */
.filter-row {
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  overflow-x: auto;
  scrollbar-width: none;
}
.filter-row::-webkit-scrollbar {
  display: none;
}
.filter-chip {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
}
.filter-chip--active {
  background: var(--f7-theme-color, #1a6b42);
  color: var(--f7-on-primary, #fff);
  box-shadow: 0 1px 3px rgba(26, 107, 66, 0.2);
}
.filter-chip--idle {
  background: var(--f7-surface-container-high, #e2eae1);
  color: var(--f7-on-surface, #161d18);
}
.filter-chip--idle:hover {
  background: var(--f7-surface-container-highest, #dce5dc);
}
.filter-chip--idle:active {
  transform: scale(0.97);
}

/* Loading / empty states */
.state-block {
  text-align: center;
  padding: 48px 16px;
}
.state-block--empty {
  color: var(--f7-on-surface-variant, #6f7a71);
}
.empty-icon {
  display: block;
  margin: 0 auto 8px;
  opacity: 0.5;
}
.empty-text {
  margin: 0;
  font-size: 14px;
}
.empty-hint {
  margin: 4px 0 0;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #6f7a71);
}

/* Document list rows */
.doc-type-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.doc-subtitle {
  display: flex;
  gap: 4px;
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.doc-visibility {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
}

/* Preview sheet */
.preview-body {
  padding: 12px 16px;
}
.preview-media {
  width: 100%;
  max-height: 45vh;
  object-fit: contain;
  border-radius: 10px;
  background: #000;
}
.preview-audio {
  width: 100%;
  margin: 24px 0;
}
.preview-iframe {
  width: 100%;
  height: 60vh;
  border: none;
  border-radius: 10px;
}
.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.preview-placeholder-text {
  font-size: 13px;
  margin-top: 12px;
}
.preview-info {
  margin-top: 12px;
}
.preview-value {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-align: right;
}
.preview-actions {
  margin-top: 4px;
}
.preview-action-row {
  display: flex;
  gap: 8px;
}
</style>
